import React, { useState, useEffect, useCallback } from 'react';
import { X, ShoppingBag, Pencil, Trash2, Plus, Save, ChevronUp, History } from 'lucide-react';
import { api, inr } from '../api';
import { toast } from 'sonner';

const istToday = () => new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 10);
const shiftDays = (dateStr, n) => {
  const d = new Date(dateStr + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const PRESETS = ['Today', 'Yesterday', 'This Week', 'This Month', 'All Time'];

function presetRange(preset) {
  const today = istToday();
  if (preset === 'Today') return [today, today];
  if (preset === 'Yesterday') { const y = shiftDays(today, -1); return [y, y]; }
  if (preset === 'This Week') {
    const dow = new Date(today + 'T00:00:00Z').getUTCDay();
    return [shiftDays(today, -((dow + 6) % 7)), today];
  }
  if (preset === 'This Month') return [today.slice(0, 8) + '01', today];
  return ['', ''];
}

function EditForm({ order, onSaved, onCancel }) {
  const [form, setForm] = useState({
    customer_name: order.customer_name || '',
    mobile_number: order.mobile_number || '',
    email: order.email || '',
    address: order.address || '',
    payment_method: order.payment_method || 'COD',
  });
  const [items, setItems] = useState((order.products?.length ? order.products : [{ name: '', qty: 1, price: 0 }]).map((p) => ({ name: p.name || '', qty: p.qty || 1, price: p.price || 0, sku: p.sku || '', image: p.image || '' })));
  const [total, setTotal] = useState(order.total || 0);
  const [saving, setSaving] = useState(false);

  const setItem = (i, k, v) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it));
    setItems(next);
    setTotal(next.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0));
  };
  const removeItem = (i) => {
    const next = items.filter((_, idx) => idx !== i);
    setItems(next);
    setTotal(next.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0));
  };

  const save = async () => {
    if (!form.customer_name.trim()) return toast.error('Customer name is required');
    if (Number(total) <= 0) return toast.error('Total must be greater than 0');
    setSaving(true);
    try {
      await api.put(`/admin/orders/${order.id}`, {
        ...form,
        total: Number(total),
        products: items.filter((it) => it.name.trim()).map((it) => ({ ...it, qty: Number(it.qty) || 1, price: Number(it.price) || 0 })),
      });
      toast.success('Order updated');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400';

  return (
    <div className="mt-3 bg-blue-50/60 border border-blue-200 rounded-xl p-3 space-y-2.5" data-testid={`order-edit-form-${order.id}`}>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Customer Name</label>
          <input data-testid="order-edit-customer-name" className={inputCls} value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Mobile</label>
          <input data-testid="order-edit-mobile" className={inputCls} value={form.mobile_number} onChange={(e) => setForm({ ...form, mobile_number: e.target.value })} />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Email</label>
          <input data-testid="order-edit-email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-slate-500">Payment</label>
          <select data-testid="order-edit-payment" className={inputCls} value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
            {['COD', 'Prepaid', 'UPI', 'Card', 'manual'].map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="text-[10px] font-bold uppercase text-slate-500">Address</label>
        <input data-testid="order-edit-address" className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] font-bold uppercase text-slate-500">Items</label>
          <button data-testid="order-edit-add-item" onClick={() => setItems([...items, { name: '', qty: 1, price: 0, sku: '', image: '' }])}
            className="flex items-center gap-1 text-[10px] font-bold text-blue-600 hover:text-blue-800">
            <Plus size={11} /> Add item
          </button>
        </div>
        <div className="space-y-1.5">
          {items.map((it, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <input data-testid={`order-edit-item-name-${i}`} className={inputCls} placeholder="Product name" value={it.name} onChange={(e) => setItem(i, 'name', e.target.value)} />
              <input data-testid={`order-edit-item-qty-${i}`} type="number" min="1" className="w-16 bg-white border border-slate-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none" value={it.qty} onChange={(e) => setItem(i, 'qty', e.target.value)} title="Qty" />
              <input data-testid={`order-edit-item-price-${i}`} type="number" min="0" className="w-24 bg-white border border-slate-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none" value={it.price} onChange={(e) => setItem(i, 'price', e.target.value)} title="Price" />
              <button data-testid={`order-edit-item-remove-${i}`} onClick={() => removeItem(i)} disabled={items.length === 1}
                className="p-1.5 rounded-full text-red-500 hover:bg-red-100 disabled:opacity-30"><Trash2 size={13} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-[10px] font-bold uppercase text-slate-500 shrink-0">Total (₹)</label>
        <input data-testid="order-edit-total" type="number" min="0" className="w-32 bg-white border border-slate-300 rounded-lg px-2.5 py-1.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-400" value={total} onChange={(e) => setTotal(e.target.value)} />
        <div className="ml-auto flex gap-2">
          <button data-testid="order-edit-cancel" onClick={onCancel} className="px-3 py-1.5 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100">Cancel</button>
          <button data-testid="order-edit-save" onClick={save} disabled={saving}
            className="flex items-center gap-1.5 bg-blue-600 text-white rounded-lg px-4 py-1.5 text-xs font-bold hover:bg-blue-500 disabled:opacity-50">
            <Save size={12} /> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminOrdersModal({ onClose, onChanged }) {
  const [preset, setPreset] = useState('Today');
  const [from, setFrom] = useState(istToday());
  const [to, setTo] = useState(istToday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [trailId, setTrailId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const istTime = (iso) => new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

  const load = useCallback(async (f, t) => {
    setLoading(true);
    try {
      const params = {};
      if (f) params.date_from = f;
      if (t) params.date_to = t;
      const r = await api.get('/admin/orders', { params });
      setData(r.data);
    } catch {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(from, to); }, []); // eslint-disable-line

  const applyPreset = (p) => {
    setPreset(p);
    const [f, t] = presetRange(p);
    setFrom(f); setTo(t);
    load(f, t);
  };

  const applyCustom = () => {
    setPreset('Custom');
    load(from, to);
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/orders/${id}`);
      toast.success('Order deleted');
      setConfirmDelete(null);
      load(from, to);
      onChanged?.();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-3 md:p-6" onClick={onClose} data-testid="admin-orders-overlay">
      <div className="bg-white rounded-2xl sticky-shadow w-full max-w-3xl max-h-[88vh] flex flex-col relative" onClick={(e) => e.stopPropagation()} data-testid="admin-orders-modal">
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-slate-100">
          <h3 className="font-display font-extrabold text-lg text-slate-900 flex items-center gap-2">
            <ShoppingBag size={18} className="text-emerald-600" /> All Orders
            {data && <span className="text-xs font-bold text-slate-400 font-sans">· {data.count} orders · {inr(data.total_value)}</span>}
          </h3>
          <button data-testid="admin-orders-close" onClick={onClose} className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500"><X size={18} /></button>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <button key={p} data-testid={`orders-filter-${p.toLowerCase().replace(/ /g, '-')}`}
              onClick={() => applyPreset(p)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-bold border-2 transition-colors ${preset === p ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}>
              {p}
            </button>
          ))}
          <div className="flex items-center gap-1.5 ml-auto">
            <input data-testid="orders-filter-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
              className="bg-white border border-slate-300 rounded-lg px-2 py-1 text-[11px] focus:outline-none" />
            <span className="text-[11px] text-slate-400 font-bold">→</span>
            <input data-testid="orders-filter-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
              className="bg-white border border-slate-300 rounded-lg px-2 py-1 text-[11px] focus:outline-none" />
            <button data-testid="orders-filter-apply" onClick={applyCustom}
              className="bg-blue-600 text-white rounded-lg px-3 py-1.5 text-[11px] font-bold hover:bg-blue-500">Apply</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin px-5 py-3 space-y-2.5" data-testid="admin-orders-list">
          {loading && <p className="font-hand text-xl text-slate-400 text-center py-8">Loading orders…</p>}
          {!loading && data?.orders?.length === 0 && (
            <p className="font-hand text-xl text-slate-400 text-center py-8" data-testid="admin-orders-empty">No orders in this period 📭</p>
          )}
          {!loading && data?.orders?.map((o) => (
            <div key={o.id} className="border-2 border-slate-200 rounded-xl p-3 hover:border-slate-300 transition-colors" data-testid={`admin-order-row-${o.id}`}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm text-slate-900 truncate">{o.customer_name} <span className="font-normal text-slate-400 text-xs">· {o.mobile_number}</span></p>
                  <p className="text-[11px] text-slate-500">
                    by {o.created_by_name || '—'} · {new Date(o.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                    {o.woo_order_id ? ` · Woo #${o.woo_order_id}` : ''} · <span className="uppercase font-semibold">{o.order_type}</span>
                  </p>
                  {o.products?.length > 0 && (
                    <p className="text-[11px] text-slate-400 truncate mt-0.5">
                      {o.products.map((p) => `${p.name}${p.qty > 1 ? ` ×${p.qty}` : ''}`).join(', ')}
                    </p>
                  )}
                </div>
                <p className="font-display font-extrabold text-base text-slate-900 shrink-0">{inr(o.total)}</p>
                <div className="flex gap-1 shrink-0">
                  {o.edit_trail?.length > 0 && (
                    <button data-testid={`admin-order-trail-${o.id}`} title={`${o.edit_trail.length} edit(s) — view history`}
                      onClick={() => setTrailId(trailId === o.id ? null : o.id)}
                      className={`relative p-2 rounded-full transition-colors ${trailId === o.id ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-600 hover:bg-amber-100'}`}>
                      <History size={13} />
                      <span className="absolute -top-1 -right-1 min-w-[14px] h-3.5 px-0.5 rounded-full bg-amber-500 text-white text-[8px] font-bold flex items-center justify-center border border-white">{o.edit_trail.length}</span>
                    </button>
                  )}
                  <button data-testid={`admin-order-edit-${o.id}`} title="Edit order"
                    onClick={() => setEditingId(editingId === o.id ? null : o.id)}
                    className={`p-2 rounded-full transition-colors ${editingId === o.id ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>
                    {editingId === o.id ? <ChevronUp size={13} /> : <Pencil size={13} />}
                  </button>
                  <button data-testid={`admin-order-delete-${o.id}`} title="Delete order"
                    onClick={() => setConfirmDelete(o)}
                    className="p-2 rounded-full bg-red-50 text-red-500 hover:bg-red-100 transition-colors">
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
              {trailId === o.id && o.edit_trail?.length > 0 && (
                <div className="mt-3 bg-amber-50/70 border border-amber-200 rounded-xl p-3 space-y-2" data-testid={`order-trail-${o.id}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1"><History size={11} /> Edit history</p>
                  {[...o.edit_trail].reverse().map((t, i) => (
                    <div key={i} className="text-[11px] text-slate-700 border-l-2 border-amber-300 pl-2">
                      <p><span className="font-bold">{t.by}</span> <span className="text-slate-400">· {istTime(t.at)}</span></p>
                      <ul className="text-slate-500 mt-0.5">
                        {(t.changes || []).map((c, j) => <li key={j}>• {c}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
              {editingId === o.id && (
                <EditForm order={o} onCancel={() => setEditingId(null)}
                  onSaved={() => { setEditingId(null); load(from, to); onChanged?.(); }} />
              )}
            </div>
          ))}

          {!loading && data?.deleted?.length > 0 && (
            <div className="pt-2" data-testid="deleted-orders-section">
              <p className="text-[10px] font-bold uppercase tracking-wider text-red-400 flex items-center gap-1 mb-1.5"><Trash2 size={11} /> Deleted orders ({data.deleted.length})</p>
              <div className="space-y-1.5">
                {data.deleted.map((d, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 bg-red-50/60 border border-red-100 rounded-lg px-3 py-2 text-[11px]" data-testid={`deleted-order-${d.order_id}`}>
                    <div className="min-w-0">
                      <p className="font-bold text-slate-700 truncate line-through decoration-red-300">{d.customer_name} <span className="font-normal text-slate-400">· {d.mobile_number}</span></p>
                      <p className="text-red-500">deleted by <span className="font-bold">{d.by}</span> · {istTime(d.at)}</p>
                    </div>
                    <p className="font-bold text-slate-500 shrink-0 line-through decoration-red-300">{inr(d.total)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {confirmDelete && (
          <div className="absolute inset-0 z-10 bg-white/80 backdrop-blur-sm flex items-center justify-center rounded-2xl" data-testid="order-delete-confirm">
            <div className="bg-white border-2 border-red-200 rounded-xl sticky-shadow p-5 max-w-sm text-center">
              <p className="font-bold text-sm text-slate-900">Delete order for {confirmDelete.customer_name}?</p>
              <p className="font-hand text-lg text-slate-500 mt-1">{inr(confirmDelete.total)} — this can't be undone</p>
              <div className="flex gap-2 justify-center mt-3">
                <button data-testid="order-delete-cancel" onClick={() => setConfirmDelete(null)}
                  className="px-4 py-2 rounded-lg text-xs font-bold text-slate-600 bg-slate-100 hover:bg-slate-200">Keep it</button>
                <button data-testid="order-delete-confirm-btn" onClick={() => del(confirmDelete.id)}
                  className="px-4 py-2 rounded-lg text-xs font-bold text-white bg-red-500 hover:bg-red-600">Yes, delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
